import {
  MediaUploader as MediaUploaderControl,
  VideoService,
  getImageContentSource
} from '@focaldata/cin-ui-components'
import React, { useContext, useEffect, useState } from 'react'
import {
  MediaEnabledEntry,
  MediaType
} from '../../../../data/model/questionnaire'
import useMediaUrlAndThumbnailUrl, {
  getImageUrl
} from '../../../../data/rest/media/media'
import uploadFile from '../../../../data/rest/media/uploadMedia'
import { WithEntry } from '../../../../utils/questionnaireUtils'
import QuestionnaireContext, {
  closeAddMediaDialog
} from '../../Questionnaire.context'
import { useSetQuestionMedia } from './MediaUploader.mutations'

interface Props extends WithEntry {
  onMediaUploadStart?: () => void
  onMediaUploadEnd?: () => void
}

const EntryMediaUploader: React.FC<Props> = ({
  entry,
  onMediaUploadStart,
  onMediaUploadEnd
}: Props) => {
  const mediaEntry = entry.entryItem as MediaEnabledEntry
  const { questionMedia } = mediaEntry

  const { dispatch, questionnaireState } = useContext(QuestionnaireContext)
  const [tempUrl, setTempUrl] = useState<string | undefined>()
  const [isMediaPresent, setIsMediaPresent] = useState<boolean>(
    !!questionMedia?.mediaUrl
  )
  const [errorOnUpload, setErrorOnUpload] = useState<boolean>(false)
  const { resizedMediaUrl, resizedThumbnailUrl } = useMediaUrlAndThumbnailUrl(
    questionMedia?.mediaUrl,
    questionMedia?.mediaType,
    questionMedia?.renderedMediaUrl
  )

  const setQuestionMedia = useSetQuestionMedia(entry.id)

  useEffect(() => {
    if (questionMedia) {
      setIsMediaPresent(true)
    }
  }, [questionMedia])

  const isDialogOpen =
    questionnaireState.openedAddMediaDialogNumber === entry.number

  if (!isDialogOpen && !isMediaPresent) {
    return null
  }

  const removeFile: () => Promise<void> = async () => {
    setIsMediaPresent(false)
    await setQuestionMedia(null)
  }

  const handleFileUpload: (file: File) => Promise<void> = async (file) => {
    try {
      setErrorOnUpload(false)
      if (onMediaUploadStart) onMediaUploadStart()
      const mediaUrl = await uploadFile(file)
      if (mediaUrl) {
        const renderedMediaUrl = await getImageUrl(mediaUrl)
        setQuestionMedia({
          mediaName: file.name,
          mediaType: MediaType.Image,
          mediaUrl,
          renderedMediaUrl
        })
      }
    } catch (error) {
      setErrorOnUpload(true)
    } finally {
      if (onMediaUploadEnd) onMediaUploadEnd()
    }
  }

  const handleRemoveFile: () => void = async () => {
    setIsMediaPresent(false)
    setTempUrl(undefined)
    await removeFile()
  }

  const handleSetMedia: (mediaUrl: string) => void = async (mediaUrl) => {
    setQuestionMedia({
      mediaName: mediaUrl,
      mediaType: MediaType.Video,
      mediaUrl
    })

    dispatch(closeAddMediaDialog())
  }

  const supportedVideoService = [VideoService.YOUTUBE, VideoService.VIMEO]

  return (
    <MediaUploaderControl
      isDialogOpened={isDialogOpen}
      shouldHideAddMediaButton
      shouldHideRemoveMediaButton={!questionMedia?.mediaUrl}
      mediaType={questionMedia?.mediaType}
      thumbnailUrl={tempUrl || resizedThumbnailUrl}
      errorOnUpload={errorOnUpload}
      isMediaPresent={isMediaPresent}
      mediaUrl={resizedMediaUrl}
      supportedVideoService={supportedVideoService}
      onFileUpload={async (file) => {
        const tempThumb = await getImageContentSource(file)
        setTempUrl(tempThumb)
        setIsMediaPresent(true)
        await handleFileUpload(file)
      }}
      onRemoveMedia={handleRemoveFile}
      onMediaUrlSet={handleSetMedia}
      onCloseDialog={() => dispatch(closeAddMediaDialog())}
      maxSizeMb={5}
    />
  )
}

export default EntryMediaUploader
